import { useCallback, useState } from 'react'
import { useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router'

import { QuantAnalystRail } from '@/app/components/QuantAnalystRail'
import { QuantIndicatorModalHost, type QuantIndicatorModalState } from '@/app/components/QuantIndicatorModalHost'
import { QuantIndicatorsRail } from '@/app/components/QuantIndicatorsRail'
import { QuantManualChart } from '@/app/components/QuantManualChart'
import { useQuantIndicatorCatalog } from '@/app/hooks/useQuantIndicatorCatalog'
import { toggleQuantIndicator, useQuantIndicators } from '@/app/hooks/useQuantIndicators'
import { useQuantMarket, type QuantMarketStatus } from '@/app/hooks/useQuantMarket'
import { QUANT_ANALYST_KEY, QUANT_INDICATORS_KEY, useQuantRail } from '@/app/hooks/useQuantRail'
import { useQuantSymbols } from '@/app/hooks/useQuantSymbols'
import type { QuantShellContext } from '@/app/pages/QuantJupyter'
import { parseTimeframe, TIMEFRAMES } from '@/app/lib/quant/candleStep'
import { baseFor } from '@/app/lib/quant/symbolBase'
import type { QuantManualSpan } from '@/app/lib/quant/manualSpan'
import { useSignalLog } from '@/app/lib/signalLog'
import { QuantMarketToolbar, type QuantMarketTone } from '@/ui/QuantMarketToolbar'
import { QuantPairDropdown } from '@/ui/QuantPairDropdown'
import type { ViewRate } from '@/app/lib/quant/viewProtocol'
import { QuantRail } from '@/ui/QuantRail'

const SPAN_OPTIONS: Array<{ value: QuantManualSpan; label: string }> = [
  { value: '120', label: '120' },
  { value: '240', label: '240' },
  { value: '600', label: '600' },
  { value: 'all', label: 'All' },
]
const RATE_OPTIONS = [
  { value: '1', label: '1 s' },
  { value: '5', label: '5 s' },
]
const TF_OPTIONS = TIMEFRAMES.map((tf) => ({ value: tf, label: tf }))

function stateLabel(status: QuantMarketStatus, latencyMs: number | null): string {
  switch (status) {
    case 'idle':
      return 'Idle'
    case 'connecting':
      return 'Connecting'
    case 'streaming':
      return 'Streaming'
    case 'warming_up':
      return 'Warming up'
    case 'closed':
      return 'Market closed'
    case 'no_feed':
      return 'No feed'
    case 'reconnecting':
      return 'Reconnecting'
    case 'failed':
      return 'View failed'
    case 'live':
      return latencyMs === null ? 'Live' : `Live · ${latencyMs} ms`
  }
}

/**
 * Manual trade (spec 036, handoff section 4): Indicators rail | toolbar + live
 * chart + readout | Analyst rail. The pair and timeframe live in the URL
 * (`/manual/:symbol?tf=`) and change with `replace`, so Back never
 * walks through them. The indicator list is the in-memory store
 * (`useQuantIndicators`); the rails' pin state is the only thing persisted
 * (`qmc:quant-inds`, `qmc:quant-analyst`).
 */
export default function QuantManual() {
  const { symbol: symbolParam = '' } = useParams<{ symbol?: string }>()
  const [search] = useSearchParams()
  const navigate = useNavigate()
  const { emit } = useSignalLog()
  const tf = parseTimeframe(search.get('tf'))
  const [span, setSpan] = useState<QuantManualSpan>('240')
  const [rate, setRate] = useState<ViewRate>(1)
  const shell = useOutletContext<QuantShellContext | undefined>()
  const symbolsState = useQuantSymbols(shell?.feed)
  const { all, active } = useQuantIndicators()
  const catalog = useQuantIndicatorCatalog()
  // While the indicator modal is open the rails ignore `[`/`]` and a peek never closes.
  const [modal, setModal] = useState<QuantIndicatorModalState | null>(null)
  const inds = useQuantRail({ storageKey: QUANT_INDICATORS_KEY, bracket: '[', suspended: modal !== null })
  const analyst = useQuantRail({ storageKey: QUANT_ANALYST_KEY, bracket: ']', suspended: modal !== null })

  const symbols = symbolsState.status === 'ready' ? symbolsState.symbols : []
  const known = symbols.find((s) => s.symbol === symbolParam.toUpperCase())
  const symbol = known?.symbol ?? null

  const market = useQuantMarket({
    symbol,
    tf,
    active,
    rate,
    onRecomputed: (info) => emit('save', `Indicators for ${info.symbol} ${info.tf} streamed - ${info.active} active.`),
  })

  const go = useCallback(
    (nextSymbol: string, nextTf: string) => {
      navigate({ pathname: `/manual/${nextSymbol}`, search: `?tf=${nextTf}` }, { replace: true })
    },
    [navigate],
  )

  const unknown = symbolsState.status === 'ready' && known === undefined
  const streaming = market.status === 'streaming'
  const count = market.candles.length
  const tone: QuantMarketTone = market.status
  const lastCandle = market.candles[market.candles.length - 1]
  const pivotInd = active.find((i) => i.type === 'pivots')
  const pivot = pivotInd !== undefined ? (market.indicators[pivotInd.id]?.last ?? null) : null
  const progress = streaming ? Math.round((count / market.barCap) * 100) : null

  return (
    <div data-quant-manual className="relative flex min-h-0 flex-1 bg-ink text-paper">
      <QuantRail
        side="left"
        label="Indicators"
        noun="indicators"
        width={250}
        open={inds.open}
        peeking={inds.peeking}
        stripCount={String(active.length)}
        headerMeta={<span className="font-mono text-[10px] text-ink-50">{active.length} on</span>}
        shortcut="["
        onToggle={inds.toggle}
        onPointerEnter={inds.onPointerEnter}
        onPointerLeave={inds.onPointerLeave}
      >
        <QuantIndicatorsRail
          indicators={all}
          catalog={catalog.status === 'ready' ? catalog.types : []}
          onToggle={toggleQuantIndicator}
          onEdit={(id) => {
            const ind = all.find((i) => i.id === id)
            if (ind) setModal({ mode: 'edit', id, type: ind.type })
          }}
          onAdd={() => setModal({ mode: 'pick' })}
        />
      </QuantRail>

      <section className="flex min-h-0 min-w-0 flex-1 flex-col">
        <QuantMarketToolbar
          pair={
            <QuantPairDropdown
              symbols={symbols}
              feedState={symbolsState.status === 'ready' && symbolsState.known ? symbolsState.feedState : null}
              emptyText="No instrument has a live feed."
              value={symbolParam === '' ? null : symbolParam.toUpperCase()}
              onPick={(next) => {
                if (next === symbol) return
                const info = symbols.find((s) => s.symbol === next)
                go(next, tf)
                emit('save', `Chart switched to ${next}${info ? ` (${info.venue})` : ''}.`)
              }}
            />
          }
          timeframes={TF_OPTIONS}
          timeframe={tf}
          onTimeframe={(next) => {
            if (next === tf) return
            go(symbolParam.toUpperCase(), next)
            emit('save', `Timeframe set to ${next} - re-streaming ${symbolParam.toUpperCase()}.`)
          }}
          spans={SPAN_OPTIONS}
          span={span}
          onSpan={setSpan}
          countLabel={`${tf} · ${count.toLocaleString('en-US')} / ${market.barCap.toLocaleString('en-US')}`}
          stateLabel={stateLabel(market.status, market.latencyMs)}
          tone={tone}
          progress={progress}
          rate={{ options: RATE_OPTIONS, value: String(rate), onChange: (v) => setRate(v === '5' ? 5 : 1) }}
        />
        {unknown ? (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 text-center">
            <p role="alert" className="font-sans text-[13px] font-bold tracking-[0.14em] uppercase">
              Unknown symbol
            </p>
            <p className="font-mono text-[11px] text-paper-50">
              {symbolParam} is not in the symbol list. Pick a pair from the dropdown above.
            </p>
          </div>
        ) : symbolsState.status === 'error' ? (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 text-center">
            <p role="alert" className="font-mono text-[11px] text-alert">
              {symbolsState.message}
            </p>
          </div>
        ) : (
          <QuantManualChart symbol={symbol ?? symbolParam.toUpperCase()} tf={tf} span={span} active={active} state={market}
            cancelled={market.cancelled}
            onReconnect={market.reconnect}
            onCancel={market.cancel}
          />
        )}
      </section>

      <QuantRail
        side="right"
        label="Analyst"
        noun="analyst"
        width={340}
        open={analyst.open}
        peeking={analyst.peeking}
        stripCount="PLANNED"
        headerMeta={
          <span className="border border-ink-25 px-[5px] py-0.5 font-mono text-[8.5px] tracking-[0.14em] text-ink-50 uppercase">
            Planned
          </span>
        }
        shortcut="]"
        onToggle={analyst.toggle}
        onPointerEnter={analyst.onPointerEnter}
        onPointerLeave={analyst.onPointerLeave}
      >
        <QuantAnalystRail
          symbol={symbolParam.toUpperCase()}
          tf={tf}
          indicatorNames={active.map((i) => i.name)}
          lastPrice={lastCandle?.close ?? null}
          pivot={pivot}
          digits={baseFor(symbolParam.toUpperCase()).dp}
        />
      </QuantRail>
      {modal !== null && (
        <QuantIndicatorModalHost
          modal={modal}
          catalog={catalog}
          indicators={all}
          symbol={symbolParam.toUpperCase()}
          onModal={setModal}
        />
      )}
    </div>
  )
}
